import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { Button, Dropdown, Modal } from "antd";
import PropTypes from "prop-types";
import { useState } from "react";
import { ReactSVG } from "react-svg";
import { getOrganizations } from "../../utils/organization";

const ActionModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  itemName = "",
  actionType = "archive",
  customDescription = null,
  confirmButtonText = null,
  confirmButtonClass = null,
  setRestoreId, // parent hook to capture selected org ID
  isLoading = false, // new prop
}) => {
  const { isLoading: authLoading, getIdTokenClaims } = useAuth0();
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [choosenItem, setChoosenItem] = useState("");

  // Fetch orgs with react-query
  const fetchOrganizations = async () => {
    const token = await getIdTokenClaims();
    const res = await getOrganizations(token.__raw);
    return res.data.accounts;
  };

  const {
    data: organizations = [],
    isLoading: orgsLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["organizations"],
    queryFn: fetchOrganizations,
    enabled: !authLoading,
  });

  const getModalConfig = () => {
    const configs = {
      archive: {
        title: "Add to Archive",
        buttonText: "Add to Archive",
        buttonClass: "bg-blue-600 text-white",
        description: `Once added, this backup will be archived and cannot be restored easily. Please confirm your action.`,
      },
      delete: {
        title: "Delete Archive",
        buttonText: "Delete Archive",
        buttonClass: "bg-red-600 text-white",
        description: `This action permanently removes this item from the archive and cannot be undone.`,
      },
      restore: {
        title: "Restore from Archive",
        buttonText: "Restore",
        buttonClass: "bg-green-600 text-white",
        description: `This will restore the selected item from archive to your active workspace.`,
      },
      deleteAccount: {
        title: "Delete Account Permanently",
        buttonText: "Delete",
        buttonClass: "bg-red-600 text-white",
        description: `This will remove all your backups and data of your account.`,
      },
    };

    return configs[actionType] || configs.archive;
  };

  const config = getModalConfig();

  const modalTitle = title || config.title;
  const btnText = confirmButtonText || config.buttonText;
  const btnClass = confirmButtonClass || config.buttonClass;
  const description = customDescription || config.description;

  const renderDropdownList = (
    <div className="max-h-60 overflow-y-auto">
      {orgsLoading ? (
        <div className="p-4 text-sm text-gray-500">
          Loading organizations...
        </div>
      ) : isError ? (
        <div className="p-4 text-sm text-red-500">
          Failed to load organizations 😢
        </div>
      ) : (
        organizations
          .filter((org) =>
            org.account.toLowerCase().includes(searchQuery.toLowerCase())
          )
          .map((item, index) => (
            <div
              key={index}
              className="py-3 px-4 hover:bg-gray-50 cursor-pointer"
              onClick={() => {
                setChoosenItem(item.account);
                setRestoreId?.(item.id);
                setDropdownVisible(false);
              }}
            >
              {item.account}
            </div>
          ))
      )}
    </div>
  );

  return (
    <Modal
      title={<div className="text-2xl">{modalTitle}</div>}
      centered
      open={isOpen}
      footer={[
        <div
          className="flex items-center justify-end gap-3"
          key="footer-buttons"
        >
          <Button
            key="cancel"
            className="px-4 text-sm py-1.5 border border-gray-200 rounded-lg"
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            key="confirm"
            className={`px-4 text-sm py-1.5 rounded-lg ${btnClass}`}
            onClick={onConfirm}
            disabled={actionType === "restore" && !choosenItem}
            loading={isLoading}
          >
            {btnText}
          </Button>
        </div>,
      ]}
      onCancel={onClose}
    >
      <div className="py-8 text-sm border-b border-gray-300 w-full">
        <p className="text-lg font-medium mb-4">
          {actionType === "deleteAccount" ? (
            "Are you sure you want to delete your account?"
          ) : (
            <>
              Are you sure you want to{" "}
              {actionType === "delete" ? "delete" : actionType}
              {itemName ? (
                <span>
                  {" "}
                  <strong>{itemName}</strong>
                </span>
              ) : (
                ""
              )}
              {actionType === "archive" ? " to the archive" : ""}?
            </>
          )}
        </p>
        <p className="text-sm text-gray-500 mb-4">{description}</p>

        {actionType === "restore" && (
          <Dropdown
            open={dropdownVisible}
            onOpenChange={(visible) => setDropdownVisible(visible)}
            placement="bottomCenter"
            trigger={["click"]}
            className="w-full"
            dropdownRender={() => (
              <div className="bg-white w-full border border-gray-300 rounded-lg shadow-lg">
                <div className="border-b border-gray-300 p-2">
                  <div className="flex items-center gap-2 rounded-lg hover:bg-gray-50 p-2">
                    <ReactSVG
                      src={"/assets/icons/search.svg"}
                      className="text-gray-400"
                    />
                    <input
                      type="text"
                      className="outline-0 bg-transparent w-full"
                      placeholder="Search by title or description..."
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
                <div className="mt-2">{renderDropdownList}</div>
              </div>
            )}
          >
            <div className="w-full cursor-pointer p-2.5 flex justify-between items-center rounded-lg border-gray-300 border text-medium">
              <div className="font-medium text-sm">
                {choosenItem || "Select organization"}
              </div>
              <ReactSVG
                src="/assets/icons/common-dropdown.svg"
                className="text-gray-700"
              />
            </div>
          </Dropdown>
        )}
      </div>
    </Modal>
  );
};

ActionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  itemName: PropTypes.string,
  actionType: PropTypes.oneOf([
    "archive",
    "delete",
    "restore",
    "deleteAccount",
  ]),
  customDescription: PropTypes.string,
  confirmButtonText: PropTypes.string,
  confirmButtonClass: PropTypes.string,
  setRestoreId: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default ActionModal;
